import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { ChevronRight, Command, LogOut, Search, ShieldCheck } from 'lucide-react';
import { Header as ShellHeader, ThemeToggle } from '@edupra/shell';
import { useAuth, logout, type PlatformRole } from '@/lib/platform-auth';
import { AdminCommandPalette } from '@/components/admin-command-palette';

const CRUMB_LABELS: Record<string, string> = {
  overview: 'Overview',
  tenants: 'Tenants',
  operators: 'Operators',
  audit: 'Audit Log',
  impersonation: 'Impersonation',
  'feature-flags': 'Feature Flags',
  billing: 'Billing',
  settings: 'Settings',
};

function pathToCrumbs(path: string): string[] {
  const seg = path.split('/').filter(Boolean);
  return seg.map(
    (s) =>
      CRUMB_LABELS[s] ??
      s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  );
}

const ROLE_STYLE: Record<
  PlatformRole,
  { bg: string; color: string; label: string }
> = {
  owner:    { bg: 'color-mix(in oklch, var(--color-accent-500) 18%, transparent)', color: 'var(--color-accent-600)', label: 'OWNER' },
  admin:    { bg: 'color-mix(in oklch, var(--color-info-500, #3b82f6) 18%, transparent)', color: 'var(--color-info-600, #2563eb)', label: 'ADMIN' },
  support:  { bg: 'color-mix(in oklch, var(--color-warn-500, #f59e0b) 18%, transparent)', color: 'var(--color-warn-600, #d97706)', label: 'SUPPORT' },
  billing:  { bg: 'color-mix(in oklch, var(--color-accent-500) 12%, transparent)', color: 'var(--fg-low)', label: 'BILLING' },
  viewer:   { bg: 'var(--bg-sunken)', color: 'var(--fg-low)', label: 'VIEWER' },
};

export function AdminHeader() {
  const { user } = useAuth();
  const location = useLocation();
  const crumbs = pathToCrumbs(location.pathname);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const initials = (user?.name ?? 'OP')
    .split(' ')
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const roleStyle = ROLE_STYLE[(user?.role as PlatformRole) ?? 'viewer'];

  const left = (
    <div className="flex items-center gap-1.5 text-[13px]">
      <span style={{ color: 'var(--fg-faint)' }}>Edupra</span>
      <ChevronRight className="h-3 w-3" style={{ color: 'var(--fg-faint)' }} />
      <span style={{ color: 'var(--fg-low)' }}>Platform</span>
      {crumbs.length === 0 ? null : (
        crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <ChevronRight className="h-3 w-3" style={{ color: 'var(--fg-faint)' }} />
            <span
              className={i === crumbs.length - 1 ? 'font-semibold tracking-tight' : ''}
              style={{
                color:
                  i === crumbs.length - 1
                    ? 'var(--fg-high)'
                    : 'var(--fg-low)',
              }}
            >
              {c}
            </span>
          </span>
        ))
      )}
    </div>
  );

  const right = (
    <>
      <button
        onClick={() => setPaletteOpen(true)}
        className="hidden md:inline-flex h-8 items-center gap-2 rounded-md border px-2.5 text-[12px] transition-colors hover:brightness-110"
        style={{
          borderColor: 'var(--border-line)',
          color: 'var(--fg-low)',
          background: 'var(--bg-surface)',
        }}
        title="Quick search (⌘K)"
      >
        <Search className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span>Search…</span>
        <span className="ed-kbd ml-2">
          <Command className="h-2.5 w-2.5" strokeWidth={2.5} />K
        </span>
      </button>

      <ThemeToggle />

      <span
        className="h-5 w-px mx-1"
        style={{ background: 'var(--border-line)' }}
      />

      {/* Operator chip + role badge */}
      <div
        className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2.5 border"
        style={{
          borderColor: 'var(--border-line)',
          background: 'var(--bg-surface)',
        }}
      >
        <div
          className="flex h-6 w-6 items-center justify-center rounded-md text-[10px] font-semibold"
          style={{ background: 'var(--fg-high)', color: 'var(--fg-inverse)' }}
        >
          {initials}
        </div>
        <div className="flex flex-col leading-tight">
          <span
            className="text-[12px] font-semibold tracking-tight"
            style={{ color: 'var(--fg-high)' }}
          >
            {user?.name ?? 'Operator'}
          </span>
          <span
            className="text-[9px] uppercase tracking-[0.08em]"
            style={{ color: 'var(--fg-low)' }}
          >
            {user?.email ?? 'platform user'}
          </span>
        </div>
        <span
          className="inline-flex items-center gap-1 ml-1 px-1.5 py-[2px] rounded text-[9px] font-semibold uppercase tracking-[0.08em]"
          style={{ background: roleStyle.bg, color: roleStyle.color }}
          title={`Role: ${roleStyle.label}`}
        >
          <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2.5} />
          {roleStyle.label}
        </span>
      </div>

      <button onClick={logout} className="ed-btn-ghost" title="Sign out">
        <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} />
      </button>
    </>
  );

  return (
    <>
      <ShellHeader left={left} right={right} />
      <AdminCommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </>
  );
}
